import {
  AnchoredPanelType,
  FormInputGroupLayout,
  FormValues,
  getValue,
  InputFormGroup,
  InputFormSidePanel,
  InputFormSidePanelComponent,
} from "o4a-react";
import * as React from "react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { NodeCountEstimator } from "../../components/NodeCountEstimator/NodeCountEstimator";
import { parseId } from "../../helpers/idHelper";
import { useMutationCall } from "../../hooks/useMutationCall";
import {
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
  MDS_HEATWAVE_UPDATE_POLL_DELAY,
  MDS_HEATWAVE_UPDATE_POLL_INTERVAL,
} from "../../models/AsyncNotificationProviders";
import { getOciRegion } from "../../utils";
import { OperationActionProps } from "../OperationTypes";

enum Fields {
  NodeCount = "nodeCount",
}

export interface NodeConfig {
  clusterSize: number | undefined;
  shapeName: string | undefined;
}

export interface MysqlEditNodeCountProps extends OperationActionProps {
  databaseId: string | undefined;
  nodeConfig: NodeConfig;
}

const MysqlEditNodeCount = (
  { targetId, location, databaseId, nodeConfig, onExecute, onCancel }: MysqlEditNodeCountProps,
): JSX.Element => {
  const { subscriptionId, resourceGroup, resourceName } = parseId(databaseId);
  const resourceGroupName = decodeURIComponent(resourceGroup || "");
  const dbSystemName = decodeURIComponent(resourceName || "");

  const [sidePanelRef, setSidePanelRef] = React.useState<InputFormSidePanelComponent>(
    {} as InputFormSidePanelComponent,
  );
  const { invokeCall: invokeHeatWaveUpdate } = useMutationCall(
    { method: apiClients.withRegion(getOciRegion(location)).mdsDatabaseApi.updateHeatWaveCluster },
  );

  const onUpdate = (formValues: FormValues): void => {
    const nodeCountValue: number = getValue(formValues, Fields.NodeCount, InputFormGroup);
    const invokeOptions = {
      onSuccess: onExecute,
      onFailure: sidePanelRef?.allowResubmit,
      notification: {
        inProgress: {
          title: Messages.notifications.inProgress.titles.updateHeatWaveNodeCount(),
          message: Messages.notifications.inProgress.messages.updateHeatWaveNodeCount(dbSystemName),
        },
        success: {
          title: Messages.notifications.success.titles.updateHeatWaveNodeCount(),
          message: Messages.notifications.success.messages.updateHeatWaveNodeCount(dbSystemName),
        },
        failure: {
          title: Messages.notifications.failure.titles.updateHeatWaveNodeCount(),
          message: Messages.notifications.failure.messages.updateHeatWaveNodeCount(dbSystemName),
        },
        asyncPolling: {
          methodKey: AsyncNotificationMethodKey.MDS_HEATWAVE_GET,
          methodArgs: {
            subscriptionId,
            resourceGroupName,
            dbSystemName,
          },
          location,
          responseMethodKey: AsyncNotificationPolledResponseKey.MDS_HEATWAVE_UPDATED_CHECK,
          delay: MDS_HEATWAVE_UPDATE_POLL_DELAY,
          interval: MDS_HEATWAVE_UPDATE_POLL_INTERVAL,
        },
      },
    };
    invokeHeatWaveUpdate({
      subscriptionId,
      resourceGroupName,
      dbSystemName,
      updateHeatWaveClusterDetails: {
        shapeName: nodeConfig.shapeName,
        clusterSize: nodeCountValue,
      },
    }, invokeOptions);
  };

  return (
    <InputFormSidePanel
      componentRef={setSidePanelRef}
      anchorId={targetId}
      type={AnchoredPanelType.LARGE}
      title={Messages.createNewPanels.nodeCountEstimator.title()}
      onSubmit={onUpdate}
      onClose={onCancel}
      layout={FormInputGroupLayout.COMPACT}
    >
      <NodeCountEstimator
        fieldName={Fields.NodeCount}
        subscriptionId={subscriptionId}
        resourceGroupName={resourceGroup}
        dbSystemName={dbSystemName}
        location={location}
        nodeCountDefaultValue={nodeConfig.clusterSize}
      />
    </InputFormSidePanel>
  );
};

export const newMysqlEditNodeCount = (
  props: MysqlEditNodeCountProps,
): JSX.Element => (<MysqlEditNodeCount {...props} />);
